import { useState, useEffect, useCallback, useRef } from "react";
import { API } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Loader2, CheckCircle2, AlertTriangle, Send, RefreshCw,
  Zap, Palette, Layers, Globe, User, Megaphone, ArrowUpRight,
  FileText, Repeat, Sparkles, GripVertical, Copy, LayoutGrid,
  Rocket, ChevronDown, ChevronUp, ShieldCheck, Eye,
} from "lucide-react";

const SLOT_META = {
  monday_anchor: { day: "Mon", full: "Monday", role: "Anchor", color: "border-t-blue-500" },
  tuesday_reactive: { day: "Tue", full: "Tuesday", role: "Reactive", color: "border-t-violet-500" },
  wednesday_anchor: { day: "Wed", full: "Wednesday", role: "Anchor", color: "border-t-blue-500" },
  thursday_conversion: { day: "Thu", full: "Thursday", role: "Conversion", color: "border-t-amber-500" },
  friday_anchor: { day: "Fri", full: "Friday", role: "Proof", color: "border-t-teal-500" },
};

const GOAL_LABELS = {
  conversion: { label: "Conv", color: "text-amber-600 bg-amber-50" },
  pipeline_nurture: { label: "Pipeline", color: "text-blue-600 bg-blue-50" },
  proof_building: { label: "Proof", color: "text-teal-600 bg-teal-50" },
  brand_awareness: { label: "Brand", color: "text-slate-500 bg-slate-50" },
};

export function WeekPlanner({ onSendMessage, onClose }) {
  const [plan, setPlan] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [dragSource, setDragSource] = useState(null);
  const [expandedSlot, setExpandedSlot] = useState(null);

  const fetchPlan = useCallback(async () => {
    setLoading(true);
    try {
      let res = await fetch(`${API}/authority-os/publishing-plan`, { credentials: "include" });
      let data = await res.json();

      if (!data.slots || data.error) {
        res = await fetch(`${API}/authority-os/publishing-plan/generate`, {
          method: "POST", credentials: "include",
        });
        data = await res.json();
      }
      setPlan(data);
      computeHealth(data);
    } catch (e) {
      console.error("WeekPlanner fetch error:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  const computeHealth = (planData) => {
    if (!planData?.slots) return;
    const slots = planData.slots.filter(s => s.status !== "empty");
    const markets = { nigeria: 0, africa: 0, uk: 0, global: 0 };
    const voices = { remi: 0, institutional: 0 };
    const offerings = new Set();
    let designerBottleneck = 0;
    let proofGaps = 0;
    let emptySlots = planData.slots.filter(s => s.status === "empty").length;
    let rexCleared = 0;
    let founderJudgement = 0;

    slots.forEach(s => {
      markets[s.market_lane || "global"]++;
      if (s.voice_system === "remi_personal") voices.remi++;
      else voices.institutional++;
      if (s.linked_offering) offerings.add(s.linked_offering);
      if ((s.designer_dependency?.items_needed || 0) > 0 && !s.designer_dependency?.designer_ready) designerBottleneck++;
      if (s.commercial_goal === "proof_building" && !s.rex_ready?.publishable) proofGaps++;
      if (s.judgement_status === "rex_cleared" || s.status === "rex_cleared") rexCleared++;
      if (s.judgement_status === "founder_judgement" || s.status === "founder_judgement") founderJudgement++;
    });

    setHealth({
      markets, voices, offerings: [...offerings],
      designerBottleneck, proofGaps, emptySlots,
      totalFilled: slots.length, rexCleared, founderJudgement,
    });
  };

  const callModify = async (action, slotKey, extra = {}) => {
    setActionLoading(slotKey || action);
    try {
      const res = await fetch(`${API}/authority-os/publishing-plan/modify`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, slot_key: slotKey, ...extra }),
      });
      const data = await res.json();
      if (data.success) await fetchPlan();
      return data;
    } catch (e) {
      console.error("Modify error:", e);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDragStart = (e, slotKey) => {
    setDragSource(slotKey);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", slotKey);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e, targetKey) => {
    e.preventDefault();
    const sourceKey = dragSource;
    setDragSource(null);
    if (sourceKey && sourceKey !== targetKey) {
      await callModify("swap_slot", sourceKey, { target_slot_key: targetKey });
    }
  };

  const handleGoLive = async () => {
    if (!plan?.slots) return;
    setActionLoading("golive");
    try {
      await callModify("approve_all");
      const designerSlots = plan.slots.filter(s =>
        s.authority_id && s.designer_dependency?.items_needed > 0
      );
      for (const slot of designerSlots) {
        onSendMessage?.(`Send ${slot.authority_id} to designer`);
      }
      const rexSlots = plan.slots.filter(s =>
        s.authority_id && s.rex_ready?.publishable && !s.designer_dependency?.items_needed
      );
      for (const slot of rexSlots) {
        onSendMessage?.(`Mark ${slot.authority_id} as ready_to_post`);
      }
      await fetchPlan();
    } finally {
      setActionLoading(null);
    }
  };

  const handleRegenerate = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/authority-os/publishing-plan/generate`, {
        method: "POST", credentials: "include",
      });
      const data = await res.json();
      setPlan(data);
      computeHealth(data);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-5 h-5 animate-spin text-slate-300" />
      </div>
    );
  }

  const slots = plan?.slots || [];
  const summary = plan?.summary || {};

  return (
    <div className="flex flex-col h-full" data-testid="week-planner">
      {/* Health Strip */}
      {health && <HealthStrip health={health} />}

      {/* Action Bar */}
      <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2 bg-white">
        <Button
          size="sm" variant="default"
          className="h-7 text-[11px] bg-blue-600 hover:bg-blue-700 text-white"
          onClick={handleGoLive}
          disabled={actionLoading === "golive"}
          data-testid="go-live-btn"
        >
          {actionLoading === "golive" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Rocket className="w-3 h-3 mr-1" />}
          Go Live
        </Button>
        <Button
          size="sm" variant="outline"
          className="h-7 text-[11px] text-green-600 border-green-200 hover:bg-green-50"
          onClick={() => callModify("approve_all")}
          disabled={!!actionLoading}
          data-testid="approve-all-btn"
        >
          <CheckCircle2 className="w-3 h-3 mr-1" /> Approve All
        </Button>
        <Button
          size="sm" variant="outline"
          className="h-7 text-[11px] text-slate-500 hover:text-slate-700 ml-auto"
          onClick={handleRegenerate}
          disabled={loading}
          data-testid="regenerate-plan-btn"
        >
          <RefreshCw className="w-3 h-3 mr-1" /> Regenerate
        </Button>
        <div className="text-[10px] text-slate-400">
          {summary.filled || 0}/{summary.total_slots || 5} filled
          {summary.rex_cleared > 0 && <span className="text-emerald-600 ml-1">{summary.rex_cleared} cleared</span>}
          {summary.founder_judgement > 0 && <span className="text-amber-600 ml-1">{summary.founder_judgement} judgement</span>}
        </div>
      </div>

      {/* 5-Column Grid */}
      <ScrollArea className="flex-1">
        <div className="grid grid-cols-5 gap-2 p-3 min-h-0" data-testid="week-grid">
          {slots.map(slot => {
            const meta = SLOT_META[slot.slot_key] || { day: "?", role: "?", color: "border-t-slate-300" };
            const isExpanded = expandedSlot === slot.slot_key;

            return (
              <div
                key={slot.slot_key}
                className={`rounded-lg border-t-[3px] border border-slate-200 bg-white flex flex-col transition-all ${meta.color} ${dragSource === slot.slot_key ? "opacity-40 scale-95" : ""} ${dragSource && dragSource !== slot.slot_key ? "ring-2 ring-blue-200 ring-dashed" : ""}`}
                draggable={slot.status !== "empty"}
                onDragStart={(e) => handleDragStart(e, slot.slot_key)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, slot.slot_key)}
                data-testid={`slot-${slot.slot_key}`}
              >
                {/* Column Header */}
                <div className="px-2.5 py-1.5 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <div className="text-[11px] font-bold text-slate-700">{meta.day}</div>
                    <div className="text-[9px] text-slate-400">{slot.time || ""} {meta.role}</div>
                  </div>
                  {slot.status !== "empty" && (
                    <GripVertical className="w-3 h-3 text-slate-300 cursor-grab" />
                  )}
                </div>

                {/* Card Content */}
                {slot.status === "empty" ? (
                  <div className="flex-1 flex items-center justify-center p-3">
                    <span className="text-[10px] text-slate-300 text-center">Empty slot<br />Drop a signal here</span>
                  </div>
                ) : (
                  <div className="p-2.5 flex flex-col gap-1.5 flex-1">
                    {/* Status Badge */}
                    <div className="flex items-center gap-1">
                      {slot.judgement_status === "rex_cleared" || slot.status === "rex_cleared" ? (
                        <span className="flex items-center gap-0.5 text-[8px] px-1.5 py-0.5 rounded-full font-bold bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid={`cleared-badge-${slot.slot_key}`}>
                          <ShieldCheck className="w-2.5 h-2.5" /> Rex Cleared
                        </span>
                      ) : slot.status === "approved" ? (
                        <span className="text-[8px] px-1.5 py-0.5 rounded-full font-bold bg-green-100 text-green-700 border border-green-200">
                          APPROVED
                        </span>
                      ) : slot.status === "burst_drafted" ? (
                        <span className="text-[8px] px-1.5 py-0.5 rounded-full font-bold bg-violet-100 text-violet-700 border border-violet-200">
                          BURST
                        </span>
                      ) : slot.judgement_status === "founder_judgement" || slot.status === "founder_judgement" ? (
                        <span className="flex items-center gap-0.5 text-[8px] px-1.5 py-0.5 rounded-full font-bold bg-amber-100 text-amber-700 border border-amber-200" data-testid={`judgement-badge-${slot.slot_key}`}>
                          <Eye className="w-2.5 h-2.5" /> Founder Judgement
                        </span>
                      ) : (
                        <span className="text-[8px] px-1.5 py-0.5 rounded-full font-bold bg-blue-100 text-blue-700">
                          DRAFT
                        </span>
                      )}
                      {slot.manually_overridden && (
                        <span className="text-[7px] px-1 py-0.5 rounded bg-slate-100 text-slate-500">overridden</span>
                      )}
                      {slot.rhythm_label && (
                        <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
                          {slot.rhythm_label}
                        </span>
                      )}
                    </div>

                    {/* Gate Failure Reasons (for Founder Judgement slots) */}
                    {(slot.judgement_status === "founder_judgement" || slot.status === "founder_judgement") && slot.gate_failures?.length > 0 && isExpanded && (
                      <div className="space-y-0.5 mt-0.5">
                        {slot.gate_failures.map((f, i) => (
                          <div key={i} className="text-[8px] text-amber-600 bg-amber-50 rounded px-1.5 py-0.5 flex items-start gap-1">
                            <AlertTriangle className="w-2.5 h-2.5 mt-0.5 shrink-0" />
                            <span>{f}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Hook / Caption */}
                    <p className="text-[11px] text-slate-800 font-medium leading-tight line-clamp-3">
                      {(slot.hook_alternatives?.[0] || slot.caption_draft || slot.signal_preview || "").slice(0, 80)}
                    </p>

                    {/* Meta Row */}
                    <div className="flex flex-wrap gap-1">
                      <span className="text-[8px] px-1.5 py-0.5 rounded bg-slate-50 text-slate-500 border border-slate-100">
                        {slot.voice_label?.split(" ")[0] || "Remi"}
                      </span>
                      <span className={`text-[8px] px-1.5 py-0.5 rounded border border-transparent ${slot.market_lane === "nigeria" ? "bg-emerald-50 text-emerald-600" : slot.market_lane === "africa" ? "bg-amber-50 text-amber-600" : slot.market_lane === "uk" ? "bg-sky-50 text-sky-600" : "bg-slate-50 text-slate-500"}`}>
                        {(slot.market_lane || "global").toUpperCase()}
                      </span>
                      {slot.linked_offering && (
                        <span className="text-[8px] px-1.5 py-0.5 rounded bg-rose-50 text-rose-600">
                          {slot.linked_offering}
                        </span>
                      )}
                    </div>

                    {/* Asset Row */}
                    <div className="flex items-center gap-1.5">
                      {slot.rex_ready?.publishable && (
                        <span className="flex items-center gap-0.5 text-[8px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full" data-testid={`rex-badge-${slot.slot_key}`}>
                          <Zap className="w-2.5 h-2.5" /> {slot.rex_ready.asset_count} Rex
                        </span>
                      )}
                      {(slot.designer_dependency?.items_needed || 0) > 0 && (
                        <span className="flex items-center gap-0.5 text-[8px] text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded-full" data-testid={`designer-badge-${slot.slot_key}`}>
                          <Palette className="w-2.5 h-2.5" /> {slot.designer_dependency.items_needed} Design
                        </span>
                      )}
                      {slot.commercial_goal && slot.commercial_goal !== "brand_awareness" && (
                        <span className={`text-[8px] px-1.5 py-0.5 rounded-full ${GOAL_LABELS[slot.commercial_goal]?.color || ""}`}>
                          {GOAL_LABELS[slot.commercial_goal]?.label || slot.commercial_goal}
                        </span>
                      )}
                    </div>

                    {/* Series / Pattern */}
                    {slot.series_context && (
                      <div className="flex items-center gap-1 text-[8px] text-indigo-500">
                        <Layers className="w-2.5 h-2.5" />
                        <span className="truncate">{typeof slot.matched_pattern === 'string' ? slot.matched_pattern : (slot.matched_pattern?.pattern_name || "Series")}</span>
                      </div>
                    )}

                    {/* Expand Toggle */}
                    <button
                      onClick={(e) => { e.stopPropagation(); setExpandedSlot(isExpanded ? null : slot.slot_key); }}
                      className="text-[9px] text-slate-400 hover:text-blue-500 flex items-center gap-0.5 mt-0.5"
                      data-testid={`expand-${slot.slot_key}`}
                    >
                      {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      {isExpanded ? "Less" : "More"}
                    </button>

                    {/* Expanded Detail */}
                    {isExpanded && (
                      <div className="mt-1 pt-1.5 border-t border-slate-100 space-y-1.5 animate-in slide-in-from-top-1 duration-150">
                        {slot.caption_draft && (
                          <div>
                            <div className="text-[8px] font-bold text-slate-400 uppercase tracking-wider">Caption</div>
                            <p className="text-[10px] text-slate-700 leading-snug">{slot.caption_draft.slice(0, 160)}</p>
                          </div>
                        )}
                        {slot.cta && (
                          <div>
                            <div className="text-[8px] font-bold text-slate-400 uppercase tracking-wider">CTA</div>
                            <p className="text-[10px] text-slate-600">{slot.cta.slice(0, 80)}</p>
                          </div>
                        )}
                        {slot.staff_amplification_prompt && (
                          <div>
                            <div className="text-[8px] font-bold text-slate-400 uppercase tracking-wider">Staff Prompt</div>
                            <p className="text-[10px] text-slate-600">{slot.staff_amplification_prompt.slice(0, 80)}</p>
                          </div>
                        )}
                        {slot.cross_market_variant && (
                          <div className="text-[9px] text-indigo-500 bg-indigo-50 rounded px-2 py-1">
                            <Globe className="w-3 h-3 inline mr-1" />{slot.cross_market_variant}
                          </div>
                        )}
                        {slot.burst && (
                          <div className="space-y-1">
                            <div className="text-[8px] font-bold text-violet-500 uppercase tracking-wider">3-Part Burst</div>
                            {slot.burst.map(b => (
                              <div key={b.burst_part} className="text-[9px] text-slate-600 bg-violet-50 rounded px-2 py-0.5">
                                <span className="font-bold">Part {b.burst_part}:</span> {b.burst_label}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Slot Actions */}
                    <div className="flex flex-wrap gap-1 mt-auto pt-1.5 border-t border-slate-50">
                      {/* Judgement Override */}
                      {(slot.judgement_status === "rex_cleared" || slot.status === "rex_cleared") && slot.status !== "approved" && (
                        <button
                          onClick={(e) => { e.stopPropagation(); callModify("override_judgement", slot.slot_key); }}
                          className="text-[8px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 hover:bg-amber-100 font-medium transition-colors"
                          disabled={!!actionLoading}
                          data-testid={`reserve-${slot.slot_key}`}
                        >
                          <Eye className="w-2.5 h-2.5 inline mr-0.5" />Reserve
                        </button>
                      )}
                      {(slot.judgement_status === "founder_judgement" || slot.status === "founder_judgement") && (
                        <button
                          onClick={(e) => { e.stopPropagation(); callModify("override_judgement", slot.slot_key); }}
                          className="text-[8px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 hover:bg-emerald-100 font-medium transition-colors"
                          disabled={!!actionLoading}
                          data-testid={`clear-${slot.slot_key}`}
                        >
                          <ShieldCheck className="w-2.5 h-2.5 inline mr-0.5" />Clear
                        </button>
                      )}
                      {(slot.status === "drafted" || slot.status === "founder_judgement" || slot.status === "rex_cleared") && (
                        <button
                          onClick={(e) => { e.stopPropagation(); callModify("approve_slot", slot.slot_key); }}
                          className="text-[8px] px-1.5 py-0.5 rounded bg-green-50 text-green-600 hover:bg-green-100 font-medium transition-colors"
                          disabled={actionLoading === slot.slot_key}
                          data-testid={`approve-${slot.slot_key}`}
                        >
                          <CheckCircle2 className="w-2.5 h-2.5 inline mr-0.5" />Approve
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); callModify("regenerate_caption", slot.slot_key); }}
                        className="text-[8px] px-1.5 py-0.5 rounded bg-slate-50 text-slate-500 hover:bg-slate-100 font-medium transition-colors"
                        disabled={!!actionLoading}
                        data-testid={`regen-${slot.slot_key}`}
                      >
                        <RefreshCw className="w-2.5 h-2.5 inline mr-0.5" />Regen
                      </button>
                      {(slot.designer_dependency?.items_needed || 0) > 0 && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onSendMessage?.(`Send ${slot.authority_id} to designer`); }}
                          className="text-[8px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-600 hover:bg-purple-100 font-medium transition-colors"
                          data-testid={`send-designer-${slot.slot_key}`}
                        >
                          <Send className="w-2.5 h-2.5 inline mr-0.5" />Designer
                        </button>
                      )}
                      {slot.rex_ready?.publishable && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onSendMessage?.(`Mark ${slot.authority_id} as ready_to_post`); }}
                          className="text-[8px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 hover:bg-emerald-100 font-medium transition-colors"
                          data-testid={`publish-text-${slot.slot_key}`}
                        >
                          <Zap className="w-2.5 h-2.5 inline mr-0.5" />Publish Text
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); callModify("switch_voice", slot.slot_key); }}
                        className="text-[8px] px-1.5 py-0.5 rounded bg-slate-50 text-slate-500 hover:bg-slate-100 font-medium transition-colors"
                        disabled={!!actionLoading}
                        data-testid={`switch-voice-${slot.slot_key}`}
                      >
                        <User className="w-2.5 h-2.5 inline mr-0.5" />Voice
                      </button>
                      {slot.status !== "burst_drafted" && (
                        <button
                          onClick={(e) => { e.stopPropagation(); callModify("burst", slot.slot_key); }}
                          className="text-[8px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-600 hover:bg-violet-100 font-medium transition-colors"
                          disabled={!!actionLoading}
                          data-testid={`burst-${slot.slot_key}`}
                        >
                          <LayoutGrid className="w-2.5 h-2.5 inline mr-0.5" />Burst
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); callModify("switch_voice", slot.slot_key); onSendMessage?.(`Duplicate ${slot.authority_id} for RESILIGENT repost`); }}
                        className="text-[8px] px-1.5 py-0.5 rounded bg-slate-50 text-slate-500 hover:bg-slate-100 font-medium transition-colors"
                        data-testid={`duplicate-${slot.slot_key}`}
                      >
                        <Copy className="w-2.5 h-2.5 inline mr-0.5" />Repost
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}

function HealthStrip({ health }) {
  const bars = [
    { label: "NG", count: health.markets.nigeria, color: "bg-emerald-500", max: 5 },
    { label: "AF", count: health.markets.africa, color: "bg-amber-500", max: 5 },
    { label: "UK", count: health.markets.uk, color: "bg-sky-500", max: 5 },
    { label: "GL", count: health.markets.global, color: "bg-slate-400", max: 5 },
  ];

  const warnings = [];
  if (health.markets.nigeria === 0 && health.totalFilled > 0) warnings.push("No Nigeria content");
  if (health.voices.institutional === 0 && health.totalFilled > 1) warnings.push("No institutional voice");
  if (health.designerBottleneck >= 3) warnings.push(`${health.designerBottleneck} slots need designer`);
  if (health.proofGaps > 0) warnings.push(`${health.proofGaps} proof gaps`);
  if (health.emptySlots > 0) warnings.push(`${health.emptySlots} empty`);
  if (health.rexCleared > 3) warnings.push("Over-cleared risk — review mix");

  return (
    <div className="px-3 py-2 bg-slate-50/80 border-b border-slate-100" data-testid="health-strip">
      <div className="flex items-center gap-3 flex-wrap">
        {/* Judgement Status */}
        <div className="flex items-center gap-1.5">
          {health.rexCleared > 0 && (
            <span className="flex items-center gap-0.5 text-[9px] px-2 py-0.5 rounded-full font-bold bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid="cleared-count">
              <ShieldCheck className="w-3 h-3" /> {health.rexCleared} Rex Cleared
            </span>
          )}
          {health.founderJudgement > 0 && (
            <span className="flex items-center gap-0.5 text-[9px] px-2 py-0.5 rounded-full font-bold bg-amber-100 text-amber-700 border border-amber-200" data-testid="judgement-count">
              <Eye className="w-3 h-3" /> {health.founderJudgement} Judgement
            </span>
          )}
        </div>

        {/* Market Balance */}
        <div className="flex items-center gap-2">
          <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Markets</span>
          <div className="flex gap-1">
            {bars.map(b => (
              <div key={b.label} className="flex items-center gap-0.5">
                <div className="w-8 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                  <div className={`h-full ${b.color} rounded-full transition-all`} style={{ width: `${(b.count / b.max) * 100}%` }} />
                </div>
                <span className="text-[8px] text-slate-500">{b.label}{b.count > 0 ? ` ${b.count}` : ""}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Voice Balance */}
        <div className="flex items-center gap-1.5">
          <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Voice</span>
          <div className="flex items-center gap-1">
            <span className="text-[8px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">R {health.voices.remi}</span>
            <span className="text-[8px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">I {health.voices.institutional}</span>
          </div>
        </div>

        {/* Offerings */}
        {health.offerings.length > 0 && (
          <div className="flex items-center gap-1">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Offers</span>
            {health.offerings.slice(0, 3).map(o => (
              <span key={o} className="text-[8px] px-1.5 py-0.5 rounded bg-rose-50 text-rose-500">{o.split(" ")[0]}</span>
            ))}
          </div>
        )}

        {/* Warnings */}
        {warnings.length > 0 && (
          <div className="flex items-center gap-1 ml-auto">
            <AlertTriangle className="w-3 h-3 text-amber-500" />
            {warnings.map((w, i) => (
              <span key={i} className="text-[8px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">{w}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
